import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Badge, Button, Card, EmptyState, ErrorText, Field, Input, Select, Spinner, StatTile, Table } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { clp, formatTime } from '../../utils/format'
import type { Professional, ProductionReport } from '../../types/api'

/**
 * "Mi producción" (H6). Pedido explícito del cliente: en su HIS actual existía
 * un reporte de producción del día, lo quitaron y lo echa de menos porque era
 * la forma transparente de verificar sus honorarios.
 *
 * El honorario aparece separado en devengado (el cargo ya se pagó) y estimado
 * (todavía no), que es justo la diferencia que el profesional necesita ver.
 */

/** Fecha de hoy en formato AAAA-MM-DD, en hora local. */
function todayValue(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function ProduccionPage() {
  const user = useAuthStore((s) => s.user)
  const queryClient = useQueryClient()
  const [from, setFrom] = useState(todayValue())
  const [to, setTo] = useState('')
  const [professionalId, setProfessionalId] = useState('')
  const [error, setError] = useState('')

  const isDoctor = user?.role === 'DOCTOR'

  const professionals = useQuery({
    queryKey: ['professionals'],
    queryFn: async () => (await api.get<Professional[]>('/professionals')).data,
    enabled: !isDoctor,
  })

  const production = useQuery({
    queryKey: ['production', from, to, professionalId],
    queryFn: async () =>
      (
        await api.get<ProductionReport>('/accounting/production', {
          params: { from, ...(to ? { to } : {}), ...(professionalId ? { professionalId } : {}) },
        })
      ).data,
    enabled: isDoctor || Boolean(professionalId),
  })

  /** Profesional actual, para leer y togglear la preferencia de correo. */
  const me = useQuery({
    queryKey: ['my-professional'],
    queryFn: async () => {
      const all = (await api.get<Professional[]>('/professionals')).data
      return all.find((p) => p.userId === user?.id) ?? null
    },
    enabled: isDoctor,
  })

  const togglePreference = useMutation({
    mutationFn: async (value: boolean) =>
      (await api.patch('/professionals/me/preferences', { dailyProductionEmail: value })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-professional'] })
      setError('')
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const report = production.data

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{isDoctor ? 'Mi producción' : 'Producción por profesional'}</h1>
        <p className="text-sm text-steel-600">
          Atenciones realizadas, montos y honorario correspondiente. El honorario de un cargo aún no pagado es una
          estimación con las reglas vigentes.
        </p>
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Desde">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Hasta (opcional)">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          {!isDoctor && (
            <Field label="Profesional">
              <Select value={professionalId} onChange={(e) => setProfessionalId(e.target.value)}>
                <option value="">Elige un profesional…</option>
                {(professionals.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                ))}
              </Select>
            </Field>
          )}
          <Button variant="secondary" className="mb-1" onClick={() => { setFrom(todayValue()); setTo('') }}>
            Hoy
          </Button>
        </div>
      </Card>

      {isDoctor && me.data && (
        <Card>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-600"
              checked={Boolean(me.data.dailyProductionEmail)}
              disabled={togglePreference.isPending}
              onChange={(e) => togglePreference.mutate(e.target.checked)}
            />
            <span>
              <span className="font-medium text-ink">Enviarme el resumen por correo al cierre del día</span>
              <span className="block text-xs text-steel-600">
                Se envía solo los días en que tuviste atenciones, a {me.data.email ?? 'tu correo registrado'}.
                {!me.data.email && ' Falta registrar tu correo en la ficha de profesional.'}
              </span>
            </span>
          </label>
          <ErrorText>{error}</ErrorText>
        </Card>
      )}

      {production.isLoading ? (
        <Spinner />
      ) : !report ? (
        <EmptyState message="Elige un profesional para ver su producción." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile label="Atenciones" value={report.totals.attentions} accent="ink" />
            <StatTile label="Facturado" value={clp(report.totals.billed)} accent="brand" />
            <StatTile label="Cobrado" value={clp(report.totals.collected)} accent="success" />
            <StatTile label="Honorario" value={clp(report.totals.fee)} accent="brand" />
          </div>

          {report.totals.feeEstimated > 0 && (
            <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <Icon name="warning" className="h-4 w-4 shrink-0" />
              {clp(report.totals.feeEstimated)} del honorario es estimación: corresponde a atenciones cuyo cargo aún no
              se paga.
            </p>
          )}

          <Card title="Detalle">
            {report.lines.length === 0 ? (
              <EmptyState message="Sin atenciones en el período." />
            ) : (
              <Table headers={['Hora', 'Paciente', 'Prestación', 'Monto', 'Estado', 'Honorario']}>
                {report.lines.map((l) => (
                  <tr key={l.appointmentId}>
                    <td className="px-2 py-2">{formatTime(l.startsAt)}</td>
                    <td className="px-2 py-2">{l.patientName}</td>
                    <td className="px-2 py-2">{l.serviceName}</td>
                    <td className="px-2 py-2">{clp(l.amount)}</td>
                    <td className="px-2 py-2">
                      <Badge className={l.paid ? 'bg-emerald-100 text-emerald-800' : 'bg-steel-200 text-steel-800'}>
                        {l.paid ? 'Pagado' : 'Pendiente'}
                      </Badge>
                    </td>
                    <td className="px-2 py-2">
                      {clp(l.fee)}
                      {l.feeIsEstimate && <span className="ml-1 text-xs text-amber-700">(estimado)</span>}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </>
      )}
    </div>
  )
}
